import React, { useState } from 'react';
import { Calendar, Clock, Settings, Save, Plus, Trash2, Users, BarChart3, Home } from 'lucide-react';
import LeadManagement from '../components/LeadManagement';
import BookingManagement from '../components/BookingManagement';
import AnalyticsDashboard from '../components/AnalyticsDashboard';

interface TimeSlot {
  id: string;
  time: string;
  available: boolean;
  booked: boolean;
}

interface Availability {
  date: string;
  slots: TimeSlot[];
}

type AdminTab = 'dashboard' | 'leads' | 'bookings' | 'availability' | 'analytics';

const AdminPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<AdminTab>('dashboard');
  const [availability, setAvailability] = useState<Availability[]>([
    {
      date: new Date().toISOString().split('T')[0],
      slots: [
        { id: '1', time: '09:00', available: true, booked: false },
        { id: '2', time: '10:00', available: true, booked: false },
        { id: '3', time: '11:00', available: false, booked: false },
        { id: '4', time: '14:00', available: true, booked: false },
        { id: '5', time: '15:00', available: true, booked: false },
        { id: '6', time: '16:00', available: false, booked: false },
      ]
    }
  ]);

  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [newTime, setNewTime] = useState('');

  const addTimeSlot = () => {
    if (!newTime) return;

    const dateStr = selectedDate;
    const existingDay = availability.find(avail => avail.date === dateStr);
    
    if (existingDay) {
      const newSlot: TimeSlot = {
        id: Date.now().toString(),
        time: newTime,
        available: true,
        booked: false
      };
      
      setAvailability(prev => prev.map(avail => 
        avail.date === dateStr 
          ? { ...avail, slots: [...avail.slots, newSlot].sort((a, b) => a.time.localeCompare(b.time)) }
          : avail
      ));
    } else {
      const newDay: Availability = {
        date: dateStr,
        slots: [{
          id: Date.now().toString(),
          time: newTime,
          available: true,
          booked: false
        }]
      };
      setAvailability(prev => [...prev, newDay]);
    }
    
    setNewTime('');
  };

  const toggleSlotAvailability = (dateStr: string, slotId: string) => {
    setAvailability(prev => prev.map(avail => 
      avail.date === dateStr 
        ? {
            ...avail,
            slots: avail.slots.map(slot => 
              slot.id === slotId 
                ? { ...slot, available: !slot.available }
                : slot
            )
          }
        : avail
    ));
  };

  const deleteSlot = (dateStr: string, slotId: string) => {
    setAvailability(prev => prev.map(avail => 
      avail.date === dateStr 
        ? {
            ...avail,
            slots: avail.slots.filter(slot => slot.id !== slotId)
          }
        : avail
    ));
  };

  const getSlotsForDate = (dateStr: string) => {
    return availability.find(avail => avail.date === dateStr)?.slots || [];
  };

  const addNewDate = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    const dateStr = newDate.toISOString().split('T')[0];
    
    if (!availability.find(avail => avail.date === dateStr)) {
      setAvailability(prev => [...prev, {
        date: dateStr,
        slots: []
      }]);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Leads</p>
                    <p className="text-2xl font-bold text-gray-900">156</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Calendar className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Bookings</p>
                    <p className="text-2xl font-bold text-gray-900">89</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <BarChart3 className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
                    <p className="text-2xl font-bold text-gray-900">57.1%</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Clock className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Today's Bookings</p>
                    <p className="text-2xl font-bold text-gray-900">12</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={() => setActiveTab('leads')}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
                >
                  <Users className="h-8 w-8 text-blue-600 mb-2" />
                  <h4 className="font-medium text-gray-900">Manage Leads</h4>
                  <p className="text-sm text-gray-500">View and manage all leads</p>
                </button>
                <button
                  onClick={() => setActiveTab('bookings')}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
                >
                  <Calendar className="h-8 w-8 text-green-600 mb-2" />
                  <h4 className="font-medium text-gray-900">Manage Bookings</h4>
                  <p className="text-sm text-gray-500">View and manage all bookings</p>
                </button>
                <button
                  onClick={() => setActiveTab('availability')}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
                >
                  <Clock className="h-8 w-8 text-purple-600 mb-2" />
                  <h4 className="font-medium text-gray-900">Set Availability</h4>
                  <p className="text-sm text-gray-500">Manage time slots and availability</p>
                </button>
              </div>
            </div>
          </div>
        );
      case 'leads':
        return <LeadManagement />;
      case 'bookings':
        return <BookingManagement />;
      case 'analytics':
        return <AnalyticsDashboard />;
      case 'availability':
        return renderAvailabilityManagement();
      default:
        return null;
    }
  };

  const renderAvailabilityManagement = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">Availability Management</h2>
        <button
          onClick={addNewDate}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add New Date
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Date Selection */}
        <div className="lg:col-span-1">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <Calendar className="h-5 w-5 mr-2" />
            Select Date
          </h3>
          
          <div className="space-y-2">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
            
            <div className="space-y-2">
              {availability.map(avail => (
                <button
                  key={avail.date}
                  onClick={() => setSelectedDate(avail.date)}
                  className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                    selectedDate === avail.date
                      ? 'bg-red-50 border-red-300 text-red-700'
                      : 'bg-white border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {new Date(avail.date).toLocaleDateString()} 
                  <span className="text-sm text-gray-500 ml-2">
                    ({avail.slots.length} slots)
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Time Slots Management */}
        <div className="lg:col-span-2">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <Clock className="h-5 w-5 mr-2" />
            Time Slots for {new Date(selectedDate).toLocaleDateString()}
          </h3>

          {/* Add New Time Slot */}
          <div className="flex gap-2 mb-6">
            <input
              type="time"
              value={newTime}
              onChange={(e) => setNewTime(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
            <button
              onClick={addTimeSlot}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Slot
            </button>
          </div>

          {/* Time Slots List */}
          <div className="space-y-2">
            {getSlotsForDate(selectedDate).map(slot => (
              <div
                key={slot.id}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  slot.available 
                    ? slot.booked 
                      ? 'bg-yellow-50 border-yellow-300' 
                      : 'bg-green-50 border-green-300'
                    : 'bg-gray-50 border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <span className="font-medium text-gray-800">{slot.time}</span>
                  <span className={`ml-3 px-2 py-1 text-xs rounded-full ${
                    slot.booked 
                      ? 'bg-yellow-200 text-yellow-800'
                      : slot.available
                      ? 'bg-green-200 text-green-800'
                      : 'bg-gray-200 text-gray-800'
                  }`}>
                    {slot.booked ? 'Booked' : slot.available ? 'Available' : 'Unavailable'}
                  </span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => toggleSlotAvailability(selectedDate, slot.id)}
                    disabled={slot.booked}
                    className={`px-3 py-1 text-sm rounded ${
                      slot.available
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {slot.available ? 'Disable' : 'Enable'}
                  </button>
                  
                  <button
                    onClick={() => deleteSlot(selectedDate, slot.id)}
                    disabled={slot.booked}
                    className="p-1 text-red-600 hover:bg-red-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
            
            {getSlotsForDate(selectedDate).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No time slots available for this date</p>
                <p className="text-sm">Add some time slots above</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-8 pt-6 border-t">
        <button className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 flex items-center">
          <Save className="h-4 w-4 mr-2" />
          Save Changes
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center">
              <Settings className="h-6 w-6 mr-2" />
              Admin Panel
            </h1>
            <div className="text-sm text-gray-500">
              Welcome back, Admin
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-lg mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {[
                { id: 'dashboard', name: 'Dashboard', icon: Home },
                { id: 'leads', name: 'Leads', icon: Users },
                { id: 'bookings', name: 'Bookings', icon: Calendar },
                { id: 'availability', name: 'Availability', icon: Clock },
                { id: 'analytics', name: 'Analytics', icon: BarChart3 }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as AdminTab)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                      activeTab === tab.id
                        ? 'border-red-500 text-red-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
